/**
 * Geo jurisdiction detection utilities for GDPR/LGPD/CCPA compliance.
 *
 * Used by:
 * - proxy.ts: sets visitor_geo cookie from Vercel Edge geo headers
 * - CookieBanner.tsx: shows consent banner only where legally required
 * - account/privacy/page.tsx: adapts rights messaging per jurisdiction
 *
 * LGAL-01, LGAL-02, LGAL-06
 */

/** EU27 member states + EEA (IS, LI, NO) + UK (post-Brexit GDPR-equivalent) */
export const GDPR_COUNTRIES = new Set([
  // EU27
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK",
  // EEA (non-EU)
  "IS",
  "LI",
  "NO",
  // UK (UK GDPR, post-Brexit)
  "GB",
]);

/** Brazil — Lei Geral de Proteção de Dados (LGPD) */
export const LGPD_COUNTRIES = new Set(["BR"]);

export type Jurisdiction = "gdpr" | "lgpd" | "ccpa" | "none";

/**
 * Returns true if the country code is subject to GDPR (EU27 + EEA + UK).
 */
export function isGDPRCountry(code: string): boolean {
  return GDPR_COUNTRIES.has(code);
}

/**
 * Returns true if the country code is subject to LGPD (Brazil).
 */
export function isLGPDCountry(code: string): boolean {
  return LGPD_COUNTRIES.has(code);
}

/**
 * Returns true if the region code is subject to CCPA (California only).
 */
export function isCCPAState(regionCode: string): boolean {
  return regionCode === "CA";
}

/**
 * Determines the applicable privacy jurisdiction for a visitor.
 * Priority: gdpr > lgpd > ccpa > none
 */
export function getJurisdiction(geo: {
  country?: string;
  region?: string;
}): Jurisdiction {
  const country = geo.country ?? "";
  const region = geo.region ?? "";

  if (isGDPRCountry(country)) return "gdpr";
  if (isLGPDCountry(country)) return "lgpd";
  if (country === "US" && isCCPAState(region)) return "ccpa";
  return "none";
}

/**
 * Returns true if the jurisdiction requires opt-in cookie consent.
 * GDPR and LGPD require consent banners; CCPA does not (opt-out model).
 */
export function requiresCookieConsent(jurisdiction: Jurisdiction): boolean {
  return jurisdiction === "gdpr" || jurisdiction === "lgpd";
}

/**
 * Reads and parses the `visitor_geo` cookie set by proxy.ts (LGAL-01).
 * Returns the geo object on success, null if missing or malformed.
 *
 * Deduplicates the parsing logic that was previously duplicated in
 * CookieBanner.tsx and account/privacy/page.tsx.
 */
export function readVisitorGeoCookie(): {
  country?: string;
  region?: string;
} | null {
  try {
    const geoCookie = document.cookie
      .split("; ")
      .find((c) => c.startsWith("visitor_geo="));
    if (!geoCookie) return null;
    return JSON.parse(
      decodeURIComponent(geoCookie.split("=").slice(1).join("=")),
    ) as { country?: string; region?: string };
  } catch {
    return null;
  }
}
